import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Users, Globe, Award, TrendingUp, MapPin, Phone, Mail } from 'lucide-react';

const About = () => {
  const stats = [
    { number: '500%', label: 'Faster market entry', description: 'Through automated partner integration' },
    { number: '95%', label: 'Improvement in supplier performance', description: 'Via intelligent management' },
    { number: '90%', label: 'Reduction in risk exposure', description: 'Through predictive risk management' },
    { number: '300%', label: 'Increase in market intelligence', description: 'Via AI-powered analysis' }
  ];

  const values = [
    {
      title: 'Innovation Excellence',
      description: 'We push the boundaries of what\'s possible with AI-driven commerce solutions.',
      icon: TrendingUp
    },
    {
      title: 'Enterprise Trust',
      description: 'Building long-term partnerships with transparency, reliability, and proven results.',
      icon: Award
    },
    {
      title: 'Global Impact',
      description: 'Transforming commerce operations across industries and continents.',
      icon: Globe
    },
    {
      title: 'Customer Success',
      description: 'Dedicated to delivering measurable value and unprecedented ROI for our clients.',
      icon: Users
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 via-purple-700 to-purple-800 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            About CredFlexi
          </h1>
          <p className="text-xl md:text-2xl text-purple-100 mb-8 max-w-4xl mx-auto">
            Revolutionizing enterprise commerce through intelligent agent orchestration since 2023.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                CredFlexi embarked on its journey with a vision to transform the B2B network landscape. Driven by a desire to simplify and optimize the B2B network, our founders set out to develop a groundbreaking solution that would empower businesses of all sizes.
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                The vision for CredFlexi is to continue leading the way in B2B commerce network innovation, empowering businesses to thrive in an increasingly digital and competitive landscape.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 transition-all duration-200 inline-flex items-center justify-center"
                >
                  Join Our Mission
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">{stat.number}</div>
                  <div className="text-lg font-semibold text-gray-900 mb-2">{stat.label}</div>
                  <div className="text-gray-600 text-sm">{stat.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-purple-50 to-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Our Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide our innovation and drive our commitment to enterprise excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-8 text-center shadow-md hover:shadow-lg transition-shadow duration-300">
                  <div className="mx-auto w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="w-8 h-8 text-purple-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Global Presence */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Global Enterprise Centers
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              24/7 enterprise support with dedicated teams and local expertise worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <MapPin className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">North America</h3>
              <div className="space-y-2 text-gray-300">
                <p>Atlanta - HQ</p>
                <p>Delaware</p>
              </div>
            </div>

            <div className="text-center">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <MapPin className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Europe</h3>
              <div className="space-y-2 text-gray-300">
                <p>London</p>
              </div>
            </div>

            <div className="text-center">
              <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
                <MapPin className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Asia-Pacific</h3>
              <div className="space-y-2 text-gray-300">
                <p>Kolkata</p>
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <h3 className="text-2xl font-bold text-white mb-4">Enterprise Direct Contact</h3>
            <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
              <div className="flex items-center space-x-2">
                <Mail className="w-5 h-5 text-purple-400" />
                <a href="mailto:info@credflexi.com" className="text-gray-300 hover:text-white transition-colors duration-200">
                  info@credflexi.com
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-5 h-5 text-purple-400" />
                <span className="text-gray-300">24/7 Global Support Available</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technology & Innovation */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Technology Excellence
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We leverage cutting-edge technologies and advanced tools to construct a platform that excels in speed, security, reliability, and 24/7 availability.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 border border-gray-200 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <div className="text-4xl font-bold text-purple-600 mb-2">99.99%</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">Uptime Guarantee</div>
              <div className="text-gray-600">Enterprise-grade reliability with global redundancy</div>
            </div>
            
            <div className="text-center p-8 border border-gray-200 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <div className="text-4xl font-bold text-purple-600 mb-2">&lt; 50ms</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">Response Time</div>
              <div className="text-gray-600">Lightning-fast processing for real-time operations</div>
            </div>
            
            <div className="text-center p-8 border border-gray-200 rounded-xl hover:shadow-lg transition-shadow duration-300">
              <div className="text-4xl font-bold text-purple-600 mb-2">1M+</div>
              <div className="text-lg font-semibold text-gray-900 mb-2">Daily Transactions</div>
              <div className="text-gray-600">Scalable architecture handling massive volumes</div>
            </div>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section id="careers" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Join Our Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Help us revolutionize enterprise commerce with cutting-edge AI technology. Remote opportunities available.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Marketing Manager */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Marketing Manager</h3>
                <div className="flex items-center space-x-4 text-gray-600 mb-4">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">Remote - USA</span>
                  <span className="text-sm">Full-time</span>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">About the Role:</h4>
                <p className="text-gray-700 mb-4">
                  Lead marketing initiatives for our AI-driven commerce platform, targeting enterprise clients across manufacturing, financial services, and healthcare industries.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Key Responsibilities:</h4>
                <ul className="space-y-2">
                  {[
                    'Develop and execute B2B marketing strategies for enterprise clients',
                    'Create compelling content for case studies, whitepapers, and thought leadership',
                    'Manage digital marketing campaigns across LinkedIn, industry publications',
                    'Collaborate with sales team on lead generation and nurturing',
                    'Analyze market trends and competitive landscape in AI/commerce space'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-purple-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Requirements:</h4>
                <ul className="space-y-1">
                  {[
                    '5+ years B2B marketing experience, preferably in SaaS/enterprise software',
                    'Experience with AI, fintech, or commerce platforms preferred',
                    'Strong content creation and campaign management skills',
                    'MBA or relevant marketing degree preferred'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-purple-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to="/contact"
                className="w-full bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-purple-800 transition-all duration-200 inline-flex items-center justify-center"
              >
                Apply Now
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>

            {/* Sales Director */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-8 shadow-md hover:shadow-lg transition-shadow duration-300">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Enterprise Sales Director</h3>
                <div className="flex items-center space-x-4 text-gray-600 mb-4">
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">Remote - USA</span>
                  <span className="text-sm">Full-time</span>
                </div>
              </div>
              
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">About the Role:</h4>
                <p className="text-gray-700 mb-4">
                  Drive enterprise sales for Fortune 500 companies seeking AI-driven commerce transformation. Focus on OEMs, financial institutions, and large-scale B2B organizations.
                </p>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Key Responsibilities:</h4>
                <ul className="space-y-2">
                  {[
                    'Manage full enterprise sales cycle from prospecting to close',
                    'Build relationships with C-level executives and decision makers',
                    'Conduct product demonstrations and proof-of-concept implementations',
                    'Collaborate with technical teams on custom enterprise solutions',
                    'Achieve quarterly sales targets of $2M+ in new business'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Requirements:</h4>
                <ul className="space-y-1">
                  {[
                    '7+ years enterprise B2B sales experience with $500K+ deal sizes',
                    'Proven track record selling to Fortune 500 companies',
                    'Experience with AI, SaaS, or enterprise software platforms',
                    'Strong presentation and negotiation skills',
                    'Existing network in manufacturing, finance, or healthcare preferred'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Link
                to="/contact"
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-200 inline-flex items-center justify-center"
              >
                Apply Now
                <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-600 mb-4">
              Don't see a role that fits? We're always looking for exceptional talent.
            </p>
            <Link
              to="/contact"
              className="text-purple-600 hover:text-purple-700 font-semibold transition-colors duration-200"
            >
              Send us your resume →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#854DFF] to-[#7A42E6]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Join Our Journey?
          </h2>
          <p className="text-xl text-[#B19CFF] mb-8 max-w-3xl mx-auto">
            Partner with us to transform your enterprise operations and unlock unprecedented growth opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-[#854DFF] hover:bg-[#854DFF]/10 font-semibold text-lg px-8 py-4 rounded-lg shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 inline-flex items-center justify-center"
            >
              Start Your Transformation
              <ArrowRight className="ml-3 h-5 w-5" />
            </Link>
            <Link
              to="/careers"
              className="border-2 border-white/30 text-white hover:bg-white/10 font-semibold text-lg px-8 py-4 rounded-lg transition-all duration-300 inline-flex items-center justify-center backdrop-blur-sm"
            >
              Join Our Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
